
import { useState } from 'react';
import { comparisonProducts } from '../../../mocks/comparison-products';

export default function ProductComparison() {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  const toggleProduct = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      if (selectedProducts.length < 3) {
        setSelectedProducts([...selectedProducts, productId]);
      }
    }
  };

  const handleCompare = () => {
    if (selectedProducts.length >= 2) {
      setShowComparison(true);
    }
  };

  const selectedProductsData = comparisonProducts.filter(p => selectedProducts.includes(p.id));

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">제품 비교</h2>
        <p className="text-gray-600 text-lg">
          최대 3개 제품을 선택하여 사양, 가격, 제조업체를 한눈에 비교하세요
        </p>
      </div>

      {/* Selection Info */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-6 mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                선택된 제품: {selectedProducts.length}/3
              </div>
              <div className="text-sm text-gray-600">
                {selectedProducts.length < 2 ? '최소 2개 이상 선택해주세요' : '비교 준비 완료'}
              </div>
            </div>
          </div>
          <button
            onClick={handleCompare}
            disabled={selectedProducts.length < 2}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className="ri-scales-line"></i>
            제품 비교하기
          </button>
        </div>
      </div>

      {!showComparison ? (
        <>
          {/* Product Selection Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {comparisonProducts.map((product) => (
              <div
                key={product.id}
                className={`bg-white border-2 rounded-2xl p-6 transition-all cursor-pointer ${
                  selectedProducts.includes(product.id)
                    ? 'border-teal-600 shadow-lg shadow-teal-600/20'
                    : 'border-gray-200 hover:border-teal-300'
                }`}
                onClick={() => toggleProduct(product.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    selectedProducts.includes(product.id) ? 'bg-teal-600' : 'bg-gray-200'
                  }`}>
                    {selectedProducts.includes(product.id) ? (
                      <i className="ri-check-line text-white"></i>
                    ) : (
                      <i className="ri-add-line text-gray-500"></i>
                    )}
                  </div>
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                    {product.category}
                  </span>
                </div>

                <div className="w-full h-48 bg-gray-100 rounded-xl mb-4 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <h3 className="font-semibold text-gray-900 mb-2">{product.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{product.manufacturer}</p>
                
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="text-sm text-gray-500">가격</div>
                  <div className="font-bold text-teal-600">{product.price}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Comparison Table */}
          <div className="mb-6">
            <button
              onClick={() => setShowComparison(false)}
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              제품 다시 선택
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b-2 border-gray-200">
                    비교 항목
                  </th>
                  {selectedProductsData.map((product) => (
                    <th key={product.id} className="px-6 py-4 text-center border-b-2 border-gray-200">
                      <div className="w-full h-32 bg-gray-100 rounded-xl mb-3 overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="font-semibold text-gray-900">{product.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">제조업체</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-sm text-gray-900">
                      {product.manufacturer}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">카테고리</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center">
                      <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                        {product.category}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">가격</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-lg font-bold text-teal-600">
                      {product.price}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">재질</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-sm text-gray-900">
                      {product.material}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">규격</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-sm text-gray-900">
                      {product.specification}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">인증</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-sm text-gray-900">
                      {product.certification}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">납품 기간</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-sm text-gray-900">
                      {product.deliveryTime}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">보증 기간</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center text-sm text-gray-900">
                      {product.warranty}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">액션</td>
                  {selectedProductsData.map((product) => (
                    <td key={product.id} className="px-6 py-4 text-center">
                      <button className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-all whitespace-nowrap">
                        <i className="ri-shopping-cart-line"></i>
                        견적 요청
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
